import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, useInView } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

// HP1 (classic) Shop by Concern — the previous overlaid tall-card treatment
// (dark gradient, text-on-image, hover "Shop concern" pill). Retained for
// HP1 while HP2 uses the newer captions-below gallery variant.
const CONCERNS = [
  {
    title: "Signs of Aging",
    descriptor: "Firmness, fine lines & elasticity",
    image: "https://www.riversol.com/cdn/shop/files/model-age.jpg?v=1776204542&width=1200",
    href: "https://www.riversol.com/collections/all/filter:signs_of_aging",
  },
  {
    title: "Redness Control",
    descriptor: "Calming visible redness & flushing",
    image: "https://www.riversol.com/cdn/shop/files/model-redness.jpg?v=1776204542&width=1200",
    href: "https://www.riversol.com/collections/all/filter:redness",
  },
  {
    title: "Dryness",
    descriptor: "Deep hydration & barrier repair",
    image: "https://www.riversol.com/cdn/shop/files/model-dry.jpg?v=1776204542&width=1200",
    href: "https://www.riversol.com/collections/all/filter:dehydration",
  },
  {
    title: "Uneven Skin Texture",
    descriptor: "Smoothing tone & refining surface",
    image: "https://www.riversol.com/cdn/shop/files/model-uneven-textures.jpg?v=1776204542&width=1200",
    href: "https://www.riversol.com/collections/all/filter:textural_irregularities",
  },
];

const Card = ({ item, index, revealed }) => {
  return (
    <motion.a
      href={item.href}
      data-testid={`concern-card-${index}`}
      initial={{ opacity: 0, y: 40 }}
      animate={revealed ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.12 + index * 0.09 }}
      className="group relative shrink-0 min-w-0 basis-[80%] sm:basis-[54%] lg:basis-[36%] xl:basis-[31%] pl-5 lg:pl-8 select-none outline-none focus-visible:[&>div:first-child]:ring-2 focus-visible:[&>div:first-child]:ring-riversol-blue"
      draggable={false}
    >
      <div className="relative aspect-[3/4.4] overflow-hidden bg-neutral-100 rounded-sm">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-[1300ms] ease-out group-hover:scale-[1.06]"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-black/5 pointer-events-none transition-opacity duration-500 group-hover:opacity-95" />
        <div className="absolute inset-x-6 lg:inset-x-7 top-6 lg:top-7 text-white">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase opacity-75">0{index + 1}</span>
        </div>
        <div className="absolute inset-x-6 lg:inset-x-7 bottom-6 lg:bottom-8 text-white">
          <h3 className="text-[32px] lg:text-[40px] leading-[1.02] tracking-[-0.02em]">{item.title}</h3>
          <p className="mt-3 text-[13px] lg:text-[14px] text-white/80 leading-relaxed max-w-[16rem]">
            {item.descriptor}
          </p>
          <span className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-white text-neutral-900 px-4 lg:px-5 py-2.5 text-[10px] lg:text-[11px] tracking-[0.16em] uppercase font-medium translate-y-0 lg:translate-y-3 lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100 transition-[opacity,transform] duration-[600ms] ease-out">
            Shop concern
            <ArrowRight size={13} strokeWidth={1.8} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </motion.a>
  );
};

export const ShopByConcernClassic = () => {
  const sectionRef = useRef(null);
  const revealed = useInView(sectionRef, { once: true, margin: "-100px" });
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", dragFree: true, containScroll: "trimSnaps", loop: false });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onScroll = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
    setProgress(Math.min(1, Math.max(0, emblaApi.scrollProgress())));
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onScroll();
    const start = () => setDragging(true);
    const end = () => setDragging(false);
    emblaApi.on("select", onScroll);
    emblaApi.on("reInit", onScroll);
    emblaApi.on("scroll", onScroll);
    emblaApi.on("pointerDown", start);
    emblaApi.on("pointerUp", end);
    return () => {
      emblaApi.off("select", onScroll);
      emblaApi.off("reInit", onScroll);
      emblaApi.off("scroll", onScroll);
      emblaApi.off("pointerDown", start);
      emblaApi.off("pointerUp", end);
    };
  }, [emblaApi, onScroll]);

  const navBtn = (enabled) =>
    `w-11 h-11 rounded-full border transition-colors flex items-center justify-center ${
      enabled
        ? "border-neutral-300 text-neutral-800 hover:border-riversol-blue hover:bg-riversol-blue hover:text-white"
        : "border-neutral-200 text-neutral-300 cursor-not-allowed"
    }`;

  return (
    <section ref={sectionRef} id="shop-by-concern" className="bg-white py-24 lg:py-32 scroll-mt-24" data-testid="home-shop-by-concern">
      <div className="max-w-[1600px] mx-auto">
        <div className="px-5 sm:px-6 lg:px-10 flex items-end justify-between gap-8 mb-10 lg:mb-14">
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={revealed ? { opacity: 1 } : {}}
              transition={{ duration: 0.6 }}
              className="hidden font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500"
            >
              Shop by concern
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={revealed ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="mt-3 text-[36px] sm:text-[40px] lg:text-[54px] leading-[1.05] tracking-[-0.02em] text-neutral-900 max-w-[720px]"
            >
              Start where your skin <span className="italic text-riversol-blue">needs it most.</span>
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={revealed ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:flex items-center gap-2 shrink-0"
          >
            <button type="button" onClick={() => emblaApi && emblaApi.scrollPrev()} disabled={!canPrev} aria-label="Previous concerns" data-testid="concern-prev" className={navBtn(canPrev)}>
              <ArrowLeft size={16} strokeWidth={1.8} />
            </button>
            <button type="button" onClick={() => emblaApi && emblaApi.scrollNext()} disabled={!canNext} aria-label="Next concerns" data-testid="concern-next" className={navBtn(canNext)}>
              <ArrowRight size={16} strokeWidth={1.8} />
            </button>
          </motion.div>
        </div>

        <div
          className={`overflow-hidden pl-0 lg:pl-2 ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
          ref={emblaRef}
          data-testid="concern-track"
        >
          <div className="flex">
            {CONCERNS.map((item, i) => (
              <Card key={item.title} item={item} index={i} revealed={revealed} />
            ))}
            <div className="shrink-0 basis-5 lg:basis-10" aria-hidden />
          </div>
        </div>

        {/* progress + drag hint */}
        <div className="px-5 sm:px-6 lg:px-10 mt-8 flex items-center gap-6">
          <div className="relative h-px flex-1 bg-neutral-200" data-testid="concern-progress">
            <motion.span
              className="absolute left-0 top-0 h-px bg-riversol-blue"
              animate={{ width: `${Math.max(0.25, progress) * 100}%` }}
              transition={{ type: "spring", stiffness: 160, damping: 26 }}
              style={{ transformOrigin: "left" }}
            />
          </div>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 whitespace-nowrap">
            <span className="lg:hidden">Swipe to explore</span>
            <span className="hidden lg:inline">Drag to explore</span>
          </span>
        </div>
      </div>
    </section>
  );
};

export default ShopByConcernClassic;
